import { StyleSheet, Platform } from 'react-native'
import type { ViewStyle } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'
import { NOTIFICATION_DROPDOWN_OVERLAY_Z_INDEX_WEB } from '../../../src/notifications/constants/notifications.constants'

const DROPDOWN_MAX_HEIGHT = 400
const ACCENT_BAR_WIDTH = 4
const AVATAR_SIZE = 40

export const getNotificationDropdownStyles = (theme: Theme, { topOffset = 0 }: { topOffset?: number } = {}) =>
  StyleSheet.create({
    overlay: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 100,
      ...Platform.select({
        web: {
          zIndex: NOTIFICATION_DROPDOWN_OVERLAY_Z_INDEX_WEB,
        },
        default: {},
      }),
    },
    /** Full-screen scrim — heavier than `theme.background.overlay` so the dropdown clearly pops above the darkened page. */
    backdropPressable: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    container: {
      position: 'absolute',
      right: 16,
      top: topOffset,
      width: 320,
      maxHeight: DROPDOWN_MAX_HEIGHT,
      backgroundColor: theme.background.cardElevated,
      borderRadius: 12,
      borderWidth: 1,
      // borderColor: theme.gray[500],
      overflow: 'hidden',
      zIndex: 101,
      ...Platform.select({
        web: {
          zIndex: NOTIFICATION_DROPDOWN_OVERLAY_Z_INDEX_WEB + 1,
        },
        default: {},
      }),
      ...Platform.select({
        web: {
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        },
        default: {
          shadowColor: theme.shadow,
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.35,
          shadowRadius: 16,
          elevation: 12,
        },
      }),
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[200],
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.black,
    },
    listWrapper: {
      position: 'relative',
      maxHeight: DROPDOWN_MAX_HEIGHT - 100,
    },
    list: {
      maxHeight: DROPDOWN_MAX_HEIGHT - 100,
    },
    /** Web: ensure touch/trackpad scrolling inside the panel */
    listWebScroll: (Platform.OS === 'web'
      ? ({ overflowY: 'scroll', WebkitOverflowScrolling: 'touch' } as unknown as ViewStyle)
      : ({} as ViewStyle)),
    scrollFade: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 56,
      pointerEvents: 'none' as never,
    },
    listContent: {
      paddingTop: theme.spacing.sm,
      paddingBottom: theme.spacing.lg,
    },
    /** Row: optional left accent bar + avatar + text column (flex: 1) + timestamp. */
    itemContainer: {
      flexDirection: 'row',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      alignItems: 'flex-start',
      minHeight: 64,
    },
    /** Unread: vertical accent bar on the far-left edge. */
    itemAccentBar: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: ACCENT_BAR_WIDTH,
      backgroundColor: theme.primary,
    },
    /** Unread: default/primary background. */
    itemContainerUnread: {
      backgroundColor: theme.background.primary,
    },
    /** Read: slightly dimmed background. */
    itemContainerRead: {
      backgroundColor: theme.gray[100],
    },
    itemRow: {
      flexDirection: 'row',
      flex: 1,
      alignItems: 'flex-start',
      gap: theme.spacing.md,
    },
    avatarWrapper: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      overflow: 'hidden',
    },
    groupImage: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
    },
    avatarFallback: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.gray[200],
      justifyContent: 'center',
      alignItems: 'center',
    },
    avatarFallbackText: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.gray[600],
    },
    textColumn: {
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
    },
    topLine: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      gap: 4,
      marginBottom: 2,
    },
    /** User/actor name - bold; primary when unread, muted when read. */
    nameText: {
      fontSize: 14,
      fontWeight: '700',
    },
    nameTextUnread: {
      color: theme.primary,
    },
    nameTextRead: {
      color: theme.gray[500],
    },
    /** Action text (e.g. "Commented on your photo") - same line as name. */
    actionText: {
      fontSize: 14,
      fontWeight: '400',
      flexShrink: 1,
    },
    actionTextUnread: {
      color: theme.gray[700],
    },
    actionTextRead: {
      color: theme.gray[500],
    },
    /** Bottom line: notification message body. */
    messageText: {
      fontSize: 13,
      lineHeight: 18,
    },
    messageTextUnread: {
      color: theme.gray[600],
    },
    messageTextRead: {
      color: theme.gray[500],
    },
    /** Timestamp - top-right, small, light grey. */
    timeWrapper: {
      alignSelf: 'flex-start',
    },
    timeText: {
      fontSize: 11,
      color: theme.gray[400],
    },
    /** Subtle separator below each row. */
    itemSeparator: {
      height: 1,
      backgroundColor: theme.gray[200],
      marginLeft: theme.spacing.lg,
    },
    /** Faded overlay for unread (kept for existing auto-read animation). */
    unreadBackgroundOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.primary,
    },
    emptyContainer: {
      paddingTop: theme.spacing.xxl,
      paddingBottom: theme.spacing.xxl,
      paddingHorizontal: theme.spacing.lg,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: 14,
      color: theme.gray[500],
    },
  })
